import React from "react";
import { HashRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, RequireAuth, useAuth } from "../providers/AuthProvider";
import { QueryProvider } from "../providers/QueryProvider";
import { ToastContainer } from "@kkp/ui";

import Login from "../../pages/Login";
import DashboardLayout from "../../pages/Dashboard/Layout";
import DashboardPage from "../../pages/Dashboard";
import RecipientsPage from "../../pages/Recipients";
import TransactionsPage from "../../pages/Transactions";
import ProfilePage from "../../pages/Profile";
import LogsPage from "../../pages/Logs";

const CompleteRegistrationPage = React.lazy(
  () => import("../../pages/Onboarding/CompleteRegistration"),
);
const ForgotPasswordPage = React.lazy(
  () => import("../../pages/Onboarding/ForgotPassword"),
);
const ResetPasswordPage = React.lazy(
  () => import("../../pages/Onboarding/ResetPassword"),
);
const BrowserConfirmationPage = React.lazy(
  () => import("../../pages/Onboarding/BrowserConfirmation"),
);
const ConfirmBrowserPage = React.lazy(
  () => import("../../pages/Onboarding/ConfirmBrowser"),
);

/**
 * AppRouter
 *
 * CSR-only routing using HashRouter (per BLUEPRINT.md) to avoid server rewrites.
 *
 * Routes:
 * - /#/login
 * - /#/browser-confirmation
 * - /#/confirm-browser?token=...
 * - /#/ -> redirect to /#/dashboard if authed else /#/login
 * - Protected:
 *   - /#/dashboard
 *   - /#/recipients
 *   - /#/transactions
 *   - /#/profile
 *   - /#/logs
 */
export function AppRouter() {
  return (
    <QueryProvider>
      <AuthProvider>
        {/* Toast UI (Flowbite-style, brand-themed) */}
        <ToastContainer position="top-right" />

        <HashRouter>
          <Routes>
            <Route path="/" element={<IndexRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route
              path="/complete-registration"
              element={
                <React.Suspense fallback={null}>
                  <CompleteRegistrationPage />
                </React.Suspense>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <React.Suspense fallback={null}>
                  <ForgotPasswordPage />
                </React.Suspense>
              }
            />
            <Route
              path="/reset-password"
              element={
                <React.Suspense fallback={null}>
                  <ResetPasswordPage />
                </React.Suspense>
              }
            />
            <Route
              path="/browser-confirmation"
              element={
                <React.Suspense fallback={null}>
                  <BrowserConfirmationPage />
                </React.Suspense>
              }
            />
            <Route
              path="/confirm-browser"
              element={
                <React.Suspense fallback={null}>
                  <ConfirmBrowserPage />
                </React.Suspense>
              }
            />

            <Route element={<RequireAuth />}>
              <Route element={<DashboardLayout />}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/recipients" element={<RecipientsPage />} />
                <Route path="/transactions" element={<TransactionsPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/logs" element={<LogsPage />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </HashRouter>
      </AuthProvider>
    </QueryProvider>
  );
}

function IndexRedirect() {
  const auth = useAuth();
  return (
    <Navigate to={auth.isAuthenticated ? "/dashboard" : "/login"} replace />
  );
}
